/* =========================================================================
   Cyanotype Negative Maker — assets/cyanotype-kit.js (bearly.art)
   Runs on the Bearly Tools shell. GA4: pushes cy_* events to window.dataLayer;
   the shared core (assets/bearly-tools-core.js) forwards them to GA4.
   Download popup + email gate: the ONE shared popup in the core
   (BT.runWithProgress). This file only passes its wording (stages, gate text,
   done/error text) and the export as `work`; the core shows progress, asks for
   the email once for ALL tools (logged-in customers skip, fail-open), handles
   × / Escape / backdrop (no file; cy_email_dismiss) and fires cy_email_view /
   cy_email_submit. cy_download_click + "Used Cyanotype" fire inside `work`, only
   once a PNG was actually produced.
   ========================================================================= */

/* ---------------- Tracking + Klaviyo helpers ---------------- */
var cyTrack = function (name, params) {
  var o = params ? JSON.parse(JSON.stringify(params)) : {};
  o.event = name;
  (window.dataLayer = window.dataLayer || []).push(o);
};

// Klaviyo events go through the core (known email, adds tool + tool_handle; no-op
// until the visitor's email is known).
var cyKlaviyoEvent = function (name, props) {
  try { if (window.BT && typeof window.BT.klaviyoEvent === "function") window.BT.klaviyoEvent(name, props); } catch (e) {}
};

/* ---------------- Cyanotype engine ---------------- */
(function () {
  "use strict";

  var root = document.getElementById("cyanotype-root");
  if (!root) return;

  // ---------- State ----------
  var state = {
    img: null,
    srcW: 0, srcH: 0,
    lum: null,
    pw: 0, ph: 0,
    noise: null,
    fiber: null,
    brushMask: null,
    tab: "print",
    settings: {
      contrast: 1, gamma: 1, bright: 0,
      mirror: false,
      exposure: 1, paper: 50,
      texture: true, brush: true
    }
  };

  var PREVIEW_MAX = 1100;

  // ---------- DOM ----------
  var $ = function (id) { return document.getElementById(id); };
  var canvas = $("cy-canvas");
  var ctx = canvas.getContext("2d");
  var wrap = $("cy-canvas-wrap");
  var dropMsg = $("cy-drop-msg");
  var exportBtn = $("cy-export-btn");
  var exportPrevBtn = $("cy-export-preview-btn");

  cyTrack("cy_tool_view", { page_location: location.href });

  // ---------- Cyanotype color ramp ----------
  var RAMP = [
    [0.00, 245, 240, 226],
    [0.12, 220, 228, 226],
    [0.30, 158, 190, 210],
    [0.52, 84, 130, 178],
    [0.74, 35, 78, 138],
    [0.90, 18, 48, 100],
    [1.00, 8, 27, 62]
  ];

  function rampColor(t, paperWarm) {
    t = t < 0 ? 0 : t > 1 ? 1 : t;
    var i = 1;
    while (i < RAMP.length - 1 && RAMP[i][0] < t) i++;
    var a = RAMP[i - 1], b = RAMP[i];
    var f = (t - a[0]) / ((b[0] - a[0]) || 1);
    var r = a[1] + (b[1] - a[1]) * f;
    var g = a[2] + (b[2] - a[2]) * f;
    var bl = a[3] + (b[3] - a[3]) * f;
    var lightness = 1 - t;
    var warmShift = (paperWarm - 0.5) * 18 * lightness * lightness;
    r += warmShift;
    bl -= warmShift * 0.9;
    return [r, g, bl];
  }

  // ---------- Tone pipeline (shared by preview + export) ----------
  function tone(l, s) {
    var a = l + s.bright / 255;
    a = (a - 0.5) * s.contrast + 0.5;
    a = a < 0 ? 0 : a > 1 ? 1 : a;
    return Math.pow(a, 1 / s.gamma);
  }

  function printDensity(adjusted, exposure) {
    var d = (1 - adjusted) * exposure;
    var fog = 0.035 * Math.max(0, exposure - 1);
    var D = 1 - Math.exp(-2.7 * (d + fog));
    return D / (1 - Math.exp(-2.7 * 2.2));
  }

  // ---------- HEIC + decode (iPhone-safe) ----------
  var heicLoader = null;
  function ensureHeic2any() {
    if (!heicLoader) {
      heicLoader = new Promise(function (res, rej) {
        var s = document.createElement("script");
        s.src = "https://cdn.jsdelivr.net/npm/heic2any@0.0.4/dist/heic2any.min.js";
        s.onload = res;
        s.onerror = function () { heicLoader = null; rej(new Error("heic2any failed to load")); };
        document.head.appendChild(s);
      });
    }
    return heicLoader;
  }

  function heicToJpeg(file) {
    return ensureHeic2any().then(function () {
      return heic2any({ blob: file, toType: "image/jpeg", quality: 0.92 });
    }).then(function (out) { return Array.isArray(out) ? out[0] : out; });
  }

  function decodeImage(blob) {
    if ("createImageBitmap" in window) {
      return createImageBitmap(blob, { imageOrientation: "from-image" }).catch(function () {
        return decodeViaImg(blob);
      });
    }
    return decodeViaImg(blob);
  }
  function decodeViaImg(blob) {
    return new Promise(function (res, rej) {
      var url = URL.createObjectURL(blob);
      var img = new Image();
      img.onload = function () { URL.revokeObjectURL(url); res(img); };
      img.onerror = function () { URL.revokeObjectURL(url); rej(new Error("decode failed")); };
      img.src = url;
    });
  }

  // ---------- Loading ----------
  $("cy-load-btn").addEventListener("click", function () { $("cy-file-input").click(); });
  $("cy-file-input").addEventListener("change", function (e) {
    if (e.target.files && e.target.files[0]) loadFile(e.target.files[0]);
  });
  ["dragover", "dragenter"].forEach(function (ev) {
    wrap.addEventListener(ev, function (e) { e.preventDefault(); wrap.classList.add("cy-dragover"); });
  });
  ["dragleave", "drop"].forEach(function (ev) {
    wrap.addEventListener(ev, function (e) { e.preventDefault(); wrap.classList.remove("cy-dragover"); });
  });
  wrap.addEventListener("drop", function (e) {
    var f = e.dataTransfer.files && e.dataTransfer.files[0];
    if (f) loadFile(f);
  });

  function loadFile(file) {
    $("cy-img-info").textContent = "Loading\u2026";
    var isHeic = /\.hei[cf]$/i.test(file.name) || /hei[cf]/i.test(file.type || "");
    var blobPromise = isHeic ? heicToJpeg(file) : Promise.resolve(file);

    blobPromise
      .then(function (blob) {
        return decodeImage(blob).catch(function (err) {
          if (!isHeic) return heicToJpeg(file).then(decodeImage); // undetected HEIC retry
          throw err;
        });
      })
      .then(function (img) {
        state.img = img;
        state.srcW = img.naturalWidth || img.width;
        state.srcH = img.naturalHeight || img.height;
        buildPreviewData();
        $("cy-img-info").textContent = file.name + " \u2014 " + state.srcW + "\u00d7" + state.srcH + "px";
        exportBtn.disabled = false;
        exportPrevBtn.disabled = false;
        dropMsg.classList.add("cy-hidden");
        canvas.classList.remove("cy-hidden");
        render();
        cyTrack("cy_file_upload", { file_type: file.type || "unknown", px_w: state.srcW, px_h: state.srcH });
        cyKlaviyoEvent("Started Cyanotype", { file_type: file.type || "unknown" });
      })
      .catch(function () {
        $("cy-img-info").textContent =
          "Couldn't read that photo. Try a JPEG or PNG, or re-share it from the Photos app (which converts it automatically).";
      });
  }

  function buildPreviewData() {
    var scale = Math.min(1, PREVIEW_MAX / Math.max(state.srcW, state.srcH));
    var pw = Math.max(1, Math.round(state.srcW * scale));
    var ph = Math.max(1, Math.round(state.srcH * scale));
    state.pw = pw; state.ph = ph;

    var off = document.createElement("canvas");
    off.width = pw; off.height = ph;
    var octx = off.getContext("2d", { willReadFrequently: true });
    octx.drawImage(state.img, 0, 0, pw, ph);
    var data = octx.getImageData(0, 0, pw, ph).data;

    var n = pw * ph;
    var lum = new Float32Array(n);
    for (var i = 0; i < n; i++) {
      var j = i * 4;
      lum[i] = (0.2126 * data[j] + 0.7152 * data[j + 1] + 0.0722 * data[j + 2]) / 255;
    }
    state.lum = lum;

    var noise = new Float32Array(n);
    for (var k = 0; k < n; k++) noise[k] = Math.random() - 0.5;
    state.noise = noise;

    state.fiber = makeValueNoise(pw, ph, 90);
    state.brushMask = makeBrushMask(pw, ph);

    canvas.width = pw;
    canvas.height = ph;
  }

  function makeValueNoise(w, h, cell) {
    var gw = Math.ceil(w / cell) + 2, gh = Math.ceil(h / cell) + 2;
    var grid = new Float32Array(gw * gh);
    for (var i = 0; i < grid.length; i++) grid[i] = Math.random();
    var out = new Float32Array(w * h);
    var smooth = function (t) { return t * t * (3 - 2 * t); };
    for (var y = 0; y < h; y++) {
      var gy = y / cell, y0 = Math.floor(gy), fy = smooth(gy - y0);
      for (var x = 0; x < w; x++) {
        var gx = x / cell, x0 = Math.floor(gx), fx = smooth(gx - x0);
        var i00 = grid[y0 * gw + x0], i10 = grid[y0 * gw + x0 + 1];
        var i01 = grid[(y0 + 1) * gw + x0], i11 = grid[(y0 + 1) * gw + x0 + 1];
        var top = i00 + (i10 - i00) * fx;
        var bot = i01 + (i11 - i01) * fx;
        out[y * w + x] = top + (bot - top) * fy - 0.5;
      }
    }
    return out;
  }

  function makeBrushMask(w, h) {
    var mask = new Float32Array(w * h);
    var m = Math.round(Math.min(w, h) * 0.035);
    var eL = makeEdge(h, m), eR = makeEdge(h, m);
    var eT = makeEdge(w, m), eB = makeEdge(w, m);
    for (var y = 0; y < h; y++) {
      var left = m + eL[y];
      var right = w - m - eR[y];
      for (var x = 0; x < w; x++) {
        var top = m + eT[x];
        var bottom = h - m - eB[x];
        var v = 1;
        var feather = m * 0.7 + 1;
        v = Math.min(v, (x - left) / feather);
        v = Math.min(v, (right - x) / feather);
        v = Math.min(v, (y - top) / feather);
        v = Math.min(v, (bottom - y) / feather);
        mask[y * w + x] = v < 0 ? 0 : v > 1 ? 1 : v;
      }
    }
    return mask;

    function makeEdge(len, mm) {
      var arr = new Float32Array(len);
      var p1 = Math.random() * 6.28, p2 = Math.random() * 6.28;
      var f1 = 0.5 + Math.random(), f2 = 3 + Math.random() * 3;
      var jitter = 0;
      for (var i = 0; i < len; i++) {
        jitter += (Math.random() - 0.5) * mm * 0.25;
        jitter *= 0.9;
        arr[i] =
          Math.sin((i / len) * 6.28 * f1 + p1) * mm * 0.5 +
          Math.sin((i / len) * 6.28 * f2 + p2) * mm * 0.3 +
          jitter;
      }
      return arr;
    }
  }

  // ---------- Rendering ----------
  var raf = 0;
  function render() {
    if (!state.lum) return;
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(function () {
      if (state.tab === "print") renderPrint();
      else renderNegative();
    });
  }

  function renderPrint() {
    var s = state.settings;
    var pw = state.pw, ph = state.ph, lum = state.lum,
        noise = state.noise, fiber = state.fiber, brushMask = state.brushMask;
    var img = ctx.createImageData(pw, ph);
    var d = img.data;
    var warm = s.paper / 100;
    var paperBase = rampColor(0, warm);

    for (var i = 0; i < lum.length; i++) {
      var adjusted = tone(lum[i], s);
      var D = printDensity(adjusted, s.exposure);

      if (s.texture) {
        var g = noise[i] * 0.045 * (1 - Math.abs(D * 2 - 1));
        var f = fiber[i] * 0.05;
        D += g + f * (0.35 + D * 0.5);
        D = D < 0 ? 0 : D > 1 ? 1 : D;
      }

      var r, g2, b;
      var cov = s.brush ? brushMask[i] : 1;
      if (cov <= 0) {
        r = paperBase[0]; g2 = paperBase[1]; b = paperBase[2];
        if (s.texture) {
          var ff = fiber[i] * 10;
          r += ff; g2 += ff; b += ff;
        }
      } else {
        var c = rampColor(D * cov, warm);
        r = c[0]; g2 = c[1]; b = c[2];
        if (s.texture) {
          var f2 = fiber[i] * 6 * (1 - D);
          r += f2; g2 += f2; b += f2;
        }
      }

      var j = i * 4;
      d[j] = r; d[j + 1] = g2; d[j + 2] = b; d[j + 3] = 255;
    }
    ctx.putImageData(img, 0, 0);
  }

  function renderNegative() {
    var s = state.settings;
    var pw = state.pw, ph = state.ph, lum = state.lum;
    var img = ctx.createImageData(pw, ph);
    var d = img.data;
    for (var y = 0; y < ph; y++) {
      for (var x = 0; x < pw; x++) {
        var sx = s.mirror ? pw - 1 - x : x;
        var adjusted = tone(lum[y * pw + sx], s);
        var v = Math.round((1 - adjusted) * 255);
        var j = (y * pw + x) * 4;
        d[j] = v; d[j + 1] = v; d[j + 2] = v; d[j + 3] = 255;
      }
    }
    ctx.putImageData(img, 0, 0);
  }

  // ---------- Download popup (shared core) ----------
  // BT.runWithProgress owns the popup, the email step and the × / Escape / backdrop
  // close. `work` saves the file and returns a promise that rejects if no PNG was made,
  // so the core closes the popup and shows EXPORT_ERR_MSG as its toast.
  function reenableExports() {
    exportBtn.disabled = !state.img;
    exportPrevBtn.disabled = !state.lum;
  }

  function runExport(kind, work) {
    exportBtn.disabled = true;
    exportPrevBtn.disabled = true;
    if (window.BT && BT.runWithProgress) {
      BT.runWithProgress({
        stages: kind === "negative"
          ? [{ p: 18, wait: 350, text: "Reading your photo\u2026" },
             { p: 52, wait: 900, text: "Inverting the tones\u2026" },
             { p: 84, wait: 900, text: "Building your full-size negative\u2026" }]
          : [{ p: 22, wait: 350, text: "Reading your photo\u2026" },
             { p: 58, wait: 800, text: "Developing the print\u2026" },
             { p: 84, wait: 800, text: "Drying the blue\u2026" }],
        gateText: "Your file is almost ready! Enter your email to get it.",
        doneText: "Your file is ready!",
        errorText: EXPORT_ERR_MSG,
        work: work,
        onEnd: function (r) {
          if (r.status === "error") $("cy-img-info").textContent = EXPORT_ERR_MSG;
          reenableExports();
        }
      }).then(function (r) { if (r.status === "busy") reenableExports(); });
    } else {
      // Old core cached: no popup, just make the file.
      Promise.resolve().then(work).catch(function (e) {
        if (window.console) console.error("[Cyanotype]", e);
        showExportError();
      }).then(reenableExports);
    }
  }

  // ---------- Export ----------
  var EXPORT_ERR_MSG = "That photo is too large to export on this device. Try a smaller photo.";
  function showExportError() {
    $("cy-img-info").textContent = EXPORT_ERR_MSG;
    try { if (window.BT && typeof window.BT.toast === "function") window.BT.toast(EXPORT_ERR_MSG); } catch (e) {}
  }

  // iOS Safari caps canvas area at ~16.7M px; a 48MP photo would throw. Keep the
  // full-size negative under that by scaling down (aspect kept) only when needed.
  var MAX_EXPORT_PX = 16e6;
  function exportSize(w, h) {
    if (w * h <= MAX_EXPORT_PX) return { w: w, h: h, scaled: false };
    var sc = Math.sqrt(MAX_EXPORT_PX / (w * h));
    return { w: Math.max(1, Math.floor(w * sc)), h: Math.max(1, Math.floor(h * sc)), scaled: true };
  }

  exportBtn.addEventListener("click", function () {
    if (!state.img) return;
    runExport("negative", function () {
      return new Promise(function (resolve, reject) {
        doExportNegative(function () {
          cyTrack("cy_download_click", { kind: "negative" });
          cyKlaviyoEvent("Used Cyanotype", { kind: "negative" });
          resolve();
        }, reject);
      });
    });
  });

  exportPrevBtn.addEventListener("click", function () {
    if (!state.lum) return;
    runExport("preview", function () {
      return new Promise(function (resolve, reject) {
        doExportPreview(function () {
          cyTrack("cy_download_click", { kind: "preview" });
          cyKlaviyoEvent("Used Cyanotype", { kind: "preview" });
          resolve();
        }, reject);
      });
    });
  });

  function doExportNegative(onSaved, onFail) {
    var s = state.settings;
    var size = exportSize(state.srcW, state.srcH);
    var w = size.w, h = size.h;
    var off = document.createElement("canvas");
    off.width = w; off.height = h;
    var octx = off.getContext("2d", { willReadFrequently: true });
    octx.drawImage(state.img, 0, 0, w, h);
    var img = octx.getImageData(0, 0, w, h);
    var d = img.data;

    var vals = new Uint8ClampedArray(w * h);
    for (var i = 0; i < w * h; i++) {
      var j = i * 4;
      var l = (0.2126 * d[j] + 0.7152 * d[j + 1] + 0.0722 * d[j + 2]) / 255;
      vals[i] = Math.round((1 - tone(l, s)) * 255);
    }
    for (var y = 0; y < h; y++) {
      for (var x = 0; x < w; x++) {
        var sx = s.mirror ? w - 1 - x : x;
        var v = vals[y * w + sx];
        var jj = (y * w + x) * 4;
        d[jj] = v; d[jj + 1] = v; d[jj + 2] = v; d[jj + 3] = 255;
      }
    }
    octx.putImageData(img, 0, 0);
    download(off, "cyanotype-negative.png", function () {
      if (size.scaled) {
        $("cy-img-info").textContent = "Negative saved at " + w + "\u00d7" + h + "px (scaled down from " +
          state.srcW + "\u00d7" + state.srcH + "px to fit this device).";
      }
      if (onSaved) onSaved();
    }, onFail);
  }

  function doExportPreview(onSaved, onFail) {
    var wasTab = state.tab;
    state.tab = "print";
    try {
      renderPrint();
      download(canvas, "cyanotype-preview.png", onSaved, onFail);
    } finally {
      state.tab = wasTab;
      render();
    }
  }

  // No blob = nothing saved: report it through onFail (the popup's error path), so no
  // cy_download_click / "Used Cyanotype" fires.
  function download(cnv, name, onSaved, onFail) {
    cnv.toBlob(function (blob) {
      if (!blob) {
        if (onFail) { onFail(new Error("toBlob returned no image")); return; }
        $("cy-img-info").textContent = "Export failed — the image may be too large for this device. Try a smaller photo.";
        return;
      }
      var url = URL.createObjectURL(blob);
      var a = document.createElement("a");
      a.href = url;
      a.rel = "noopener";
      if ("download" in a) {
        a.download = (window.BT && BT.brandName ? BT.brandName(name) : name);
        document.body.appendChild(a); // required for iOS Safari/Chrome
        a.click();
        a.remove();
      } else {
        window.open(url, "_blank");
      }
      setTimeout(function () { URL.revokeObjectURL(url); }, 10000);
      if (onSaved) { try { onSaved(); } catch (e) {} }
    }, "image/png");
  }

  // ---------- Wiring ----------
  function bindSlider(id, key, outId, fmt) {
    var el = $(id), out = $(outId);
    el.addEventListener("input", function () {
      state.settings[key] = parseFloat(el.value);
      out.textContent = fmt(el.value);
      render();
    });
  }
  bindSlider("cy-s-contrast", "contrast", "cy-v-contrast", function (v) { return (+v).toFixed(2); });
  bindSlider("cy-s-gamma", "gamma", "cy-v-gamma", function (v) { return (+v).toFixed(2); });
  bindSlider("cy-s-bright", "bright", "cy-v-bright", function (v) { return v; });
  bindSlider("cy-s-exposure", "exposure", "cy-v-exposure", function (v) { return (+v).toFixed(2); });
  bindSlider("cy-s-paper", "paper", "cy-v-paper", function (v) { return v; });

  function bindToggle(id, key) {
    var el = $(id);
    el.addEventListener("change", function () {
      state.settings[key] = el.checked;
      render();
    });
  }
  bindToggle("cy-t-mirror", "mirror");
  bindToggle("cy-t-texture", "texture");
  bindToggle("cy-t-brush", "brush");

  var tabPrint = $("cy-tab-print"), tabNeg = $("cy-tab-neg");
  function setTab(name) {
    state.tab = name;
    tabPrint.setAttribute("aria-selected", name === "print");
    tabNeg.setAttribute("aria-selected", name === "neg");
    render();
  }
  tabPrint.addEventListener("click", function () { setTab("print"); });
  tabNeg.addEventListener("click", function () { setTab("neg"); });
})();